import { Minus, Plus } from "@phosphor-icons/react";
import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export type NumberStepperProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "onChange" | "size" | "value"> & {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: "sm" | "md";
  error?: boolean;
};

const sizeClassName = {
  sm: "bfrs-h-8 bfrs-text-xs",
  md: "bfrs-h-9 bfrs-text-sm"
};

const clamp = (value: number, min?: number, max?: number) => {
  if (typeof min === "number" && value < min) return min;
  if (typeof max === "number" && value > max) return max;
  return value;
};

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(
  ({ className, value, defaultValue = 0, onValueChange, min, max, step = 1, size = "md", error, disabled, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const currentValue = value ?? internalValue;
    const commitValue = (nextValue: number) => {
      const normalized = clamp(Number.isFinite(nextValue) ? nextValue : min ?? 0, min, max);
      if (value === undefined) setInternalValue(normalized);
      onValueChange?.(normalized);
    };
    const decrementDisabled = disabled || (typeof min === "number" && currentValue <= min);
    const incrementDisabled = disabled || (typeof max === "number" && currentValue >= max);

    return (
      <div
        className={cn(
          "bfrs-inline-flex bfrs-items-center bfrs-overflow-hidden bfrs-rounded-[var(--bfrs-input-radius)] bfrs-border bfrs-bg-surface-card bfrs-text-text-primary bfrs-transition focus-within:bfrs-ring-2 focus-within:bfrs-ring-border-focus/20",
          error ? "bfrs-border-danger focus-within:bfrs-ring-danger/15" : "bfrs-border-border-control focus-within:bfrs-border-border-focus",
          disabled && "bfrs-bg-surface-muted bfrs-text-text-disabled",
          className
        )}
      >
        <button type="button" disabled={decrementDisabled} className={cn("bfrs-flex bfrs-aspect-square bfrs-items-center bfrs-justify-center bfrs-text-text-secondary hover:bfrs-bg-surface-muted disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50", sizeClassName[size])} onClick={() => commitValue(currentValue - step)} aria-label="Decrease value">
          <Minus size={14} weight="bold" />
        </button>
        <input
          ref={ref}
          type="number"
          value={currentValue}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-invalid={error || undefined}
          className={cn("bfrs-w-14 bfrs-border-x bfrs-border-border-control bfrs-bg-transparent bfrs-text-center bfrs-font-semibold bfrs-outline-none disabled:bfrs-cursor-not-allowed", sizeClassName[size])}
          onChange={(event) => commitValue(Number(event.currentTarget.value))}
          {...props}
        />
        <button type="button" disabled={incrementDisabled} className={cn("bfrs-flex bfrs-aspect-square bfrs-items-center bfrs-justify-center bfrs-text-text-secondary hover:bfrs-bg-surface-muted disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50", sizeClassName[size])} onClick={() => commitValue(currentValue + step)} aria-label="Increase value">
          <Plus size={14} weight="bold" />
        </button>
      </div>
    );
  }
);

NumberStepper.displayName = "NumberStepper";
